import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Select as AntSelect } from "antd";
import moment from "moment";
import { FilterControl, useFilterBar } from "./filter-bar";
import styles from "./filter-bar.module.scss";
import lang from "translations";

const { Option } = AntSelect;

const getTimeOptions = () =>
  Array.from({ length: 25 }, (_, hour) => ({
    value: hour,
    label: hour === 24 ? "12:00 AM" : moment().hour(hour).minute(0).format("h:mm A"),
  }));

const getTimeHourValue = (date, isEnd = false) => {
  if (!date) return isEnd ? 24 : 0;
  if (isEnd && date.hour() === 23 && date.minute() >= 59) return 24;
  return date.hour();
};

const getTimeSummary = (value, fallback) => {
  const [startDate, endDate] = value || [];
  if (!startDate || !endDate) return fallback;
  if (startDate.hour() === 0 && startDate.minute() === 0 && endDate.hour() === 23) {
    return lang.allDay;
  }
  return `${startDate.format("h:mm A")} - ${endDate.format("h:mm A")}`;
};

const FilterTimeRange = ({ id, label, onChange, value = [], disabled = false }) => {
  const { closeFilter } = useFilterBar();
  const [pendingRange, setPendingRange] = useState(value);
  const timeOptions = useMemo(() => getTimeOptions(), []);

  useEffect(() => {
    setPendingRange(value);
  }, [value]);

  const resetPendingTime = useCallback(() => setPendingRange(value), [value]);

  const setPendingTimeHour = useCallback((rangeIndex, hour) => {
    setPendingRange((currentRange) => {
      const nextRange = [...currentRange];
      const nextTime = nextRange[rangeIndex].clone();
      if (Number(hour) === 24) {
        nextTime.endOf("day");
      } else {
        nextTime.hour(Number(hour)).minute(0).second(0).millisecond(0);
      }
      nextRange[rangeIndex] = nextTime;

      if (nextRange[0].isSame(nextRange[1], "day")) {
        if (rangeIndex === 0 && nextRange[0].isAfter(nextRange[1])) {
          nextRange[1] = nextRange[0].clone();
        }
        if (rangeIndex === 1 && nextRange[1].isBefore(nextRange[0])) {
          nextRange[0] = nextRange[1].clone();
        }
      }
      return nextRange;
    });
  }, []);

  const resetAllDay = useCallback(() => {
    setPendingRange([value[0].clone().startOf("day"), value[1].clone().endOf("day")]);
  }, [value]);

  const applyTime = useCallback(() => {
    const [pendingStart, pendingEnd] = pendingRange;
    if (!pendingStart || !pendingEnd) return;
    onChange([
      value[0]
        .clone()
        .hour(pendingStart.hour())
        .minute(pendingStart.minute())
        .second(0)
        .millisecond(0),
      value[1]
        .clone()
        .hour(pendingEnd.hour())
        .minute(pendingEnd.minute())
        .second(pendingEnd.hour() === 23 ? 59 : 0)
        .millisecond(pendingEnd.hour() === 23 ? 999 : 0),
    ]);
    closeFilter();
  }, [closeFilter, onChange, pendingRange, value]);

  return (
    <FilterControl
      id={id}
      label={label}
      summary={getTimeSummary(value, label)}
      menuClassName={styles.timeMenu}
      disabled={disabled || !value[0] || !value[1]}
      onOpen={resetPendingTime}
    >
      <div className={styles.timeRow}>
        <label htmlFor={`${id}-from`}>{lang.from}</label>
        <AntSelect
          id={`${id}-from`}
          className={styles.timeSelect}
          dropdownClassName={styles.timeSelectDropdown}
          dropdownMatchSelectWidth={false}
          getPopupContainer={(trigger) => trigger.parentElement}
          listHeight={240}
          optionFilterProp="children"
          value={getTimeHourValue(pendingRange[0])}
          onChange={(hour) => setPendingTimeHour(0, hour)}
        >
          {timeOptions.slice(0, 24).map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </AntSelect>
      </div>
      <div className={styles.timeRow}>
        <label htmlFor={`${id}-to`}>{lang.toLabel}</label>
        <AntSelect
          id={`${id}-to`}
          className={styles.timeSelect}
          dropdownClassName={styles.timeSelectDropdown}
          dropdownMatchSelectWidth={false}
          getPopupContainer={(trigger) => trigger.parentElement}
          listHeight={240}
          optionFilterProp="children"
          value={getTimeHourValue(pendingRange[1], true)}
          onChange={(hour) => setPendingTimeHour(1, hour)}
        >
          {timeOptions.slice(1).map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </AntSelect>
      </div>
      <div className={styles.timeFoot}>
        <button type="button" className={styles.resetTimeButton} onClick={resetAllDay}>
          {lang.resetToAllDay}
        </button>
        <button type="button" className={styles.applyButton} onClick={applyTime}>
          {lang.apply}
        </button>
      </div>
    </FilterControl>
  );
};

export default FilterTimeRange;
